import { useMemo, useState } from 'react';
import {
  DateTimeInput,
  FilterList,
  FilterListItem,
  FilterLiveSearch,
  Labeled,
  TextInput,
  useGetList,
  useGetMany,
  useRecordContext,
  useListContext,
} from 'react-admin';
import { Funeral } from '../types';
import { Box, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LanguageIcon from '@mui/icons-material/Language';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import {
  endOfYesterday,
  startOfWeek,
  subWeeks,
  startOfToday,
  endOfToday,
  endOfMonth,
  startOfMonth,
  subMonths,
  format,
} from 'date-fns';

/**
 * The funerals filter set. Lives in a popover off the list toolbar rather than
 * a permanent sidebar — these filters are reached occasionally, and a 250px
 * column charged for them on every page view.
 */
export const FuneralFilterPanel = () => {
  const record = useRecordContext<Funeral>();
  const { setFilters, filterValues } = useListContext();

  // Keys that should be preserved when clearing filters (tab-related + archived)
  const tabFilterKeys = ['region@eq', 'region@neq', 'has_preneed@eq', 'has_preneed@neq', 'archived_at@is', 'archived_at@not.is'];

  // Sidebar lets the user pick which date column drives the date range chips below.
  const dateFieldOptions = [
    { value: 'date_time_of_death', label: 'Date of Death' },
    { value: 'created_at', label: 'Date Created' },
    { value: 'contracts_signed_at', label: 'Contract Signed' },
  ] as const;
  type DateField = (typeof dateFieldOptions)[number]['value'];
  const allDateFields: DateField[] = dateFieldOptions.map((o) => o.value);

  const initialDateField = useMemo<DateField>(() => {
    for (const f of allDateFields) {
      if (filterValues[`${f}@gte`] !== undefined || filterValues[`${f}@lte`] !== undefined) {
        return f;
      }
    }
    return 'date_time_of_death';
    // Only initialise from URL on mount; subsequent changes go through setDateField.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [dateField, setDateField] = useState<DateField>(initialDateField);
  const dateFieldLabel = dateFieldOptions.find((o) => o.value === dateField)!.label;

  const handleDateFieldChange = (event: SelectChangeEvent<DateField>) => {
    const next = event.target.value as DateField;
    // Clear any active date range filter from the previously selected field so
    // we don't carry stale @gte/@lte values into the new column.
    const cleared = { ...filterValues };
    for (const f of allDateFields) {
      delete cleared[`${f}@gte`];
      delete cleared[`${f}@lte`];
    }
    setFilters(cleared, []);
    setDateField(next);
  };

  const handleClearFilters = () => {
    // Preserve tab-related filters when clearing
    const preservedFilters: Record<string, any> = {};
    tabFilterKeys.forEach((key) => {
      if (filterValues[key] !== undefined) {
        preservedFilters[key] = filterValues[key];
      }
    });
    setFilters(preservedFilters, []);
  };

  // Active filters, ignoring the tab filters and the toolbar's own search —
  // neither is something "Clear filters" should be claiming credit for.
  const activeKeys = Object.keys(filterValues).filter(
    (key) => !tabFilterKeys.includes(key) && key !== 'combined_unaccent@ilike',
  );
  const hasActiveFilters = activeKeys.length > 0;

  const { data: directors, isLoading: isLoadingDirectors } = useGetList(
    'profiles',
    {
      filter: { role: 'director', active: true },
    }
  );

  const { data: owners, isLoading: isLoadingOwners } = useGetList('profiles', {
    filter: { role: 'coordinator', active: true },
  });

    const lastMonthStart = startOfMonth(subMonths(new Date(), 1));
const lastMonthEnd = endOfMonth(subMonths(new Date(), 1));
const lastMonthName = format(lastMonthStart, 'MMMM yyyy');

const thisMonthStart = startOfMonth(new Date());
const thisMonthEnd = endOfMonth(new Date());
const thisMonthName = format(thisMonthStart, 'MMMM yyyy');

const twoMonthsAgoStart = startOfMonth(subMonths(new Date(), 2));
const twoMonthsAgoEnd = endOfMonth(subMonths(new Date(), 2));
const twoMonthsAgoName = format(twoMonthsAgoStart, 'MMMM yyyy');

  return (
    <div className="cleo-ui">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold">Filters</span>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={handleClearFilters} className="h-7 text-xs">
            Clear all
          </Button>
        )}
      </div>
      <div className="-mx-1 max-h-[60vh] overflow-y-auto px-1">
        <FilterList label={dateFieldLabel} icon={<AccessTimeIcon />}>
          <Box sx={{ mb: 1 }}>
            <Select
              size="small"
              variant="standard"
              fullWidth
              value={dateField}
              onChange={handleDateFieldChange}
              sx={{ fontSize: '0.85rem' }}
            >
              {dateFieldOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.85rem' }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <FilterListItem
            label="Today"
            value={{
              [`${dateField}@gte`]: startOfToday().toISOString(),
              [`${dateField}@lte`]: endOfToday().toISOString(),
            }}
          />
          <FilterListItem
            label="This Week"
            value={{
              [`${dateField}@gte`]: startOfWeek(new Date()).toISOString(),
            }}
          />
          <FilterListItem
            label="Last Week"
            value={{
              [`${dateField}@gte`]: subWeeks(
                startOfWeek(new Date()),
                1
              ).toISOString(),
              [`${dateField}@lte`]: startOfWeek(new Date()).toISOString(),
            }}
          />
          <FilterListItem
            label={thisMonthName}
            value={{
              [`${dateField}@gte`]: thisMonthStart.toISOString(),
            }}
          />
          <FilterListItem
            label={lastMonthName}
            value={{
              [`${dateField}@gte`]: lastMonthStart.toISOString(),
              [`${dateField}@lte`]: lastMonthEnd.toISOString(),
            }}
          />
          <FilterListItem
            label={twoMonthsAgoName}
            value={{
              [`${dateField}@gte`]: twoMonthsAgoStart.toISOString(),
              [`${dateField}@lte`]: twoMonthsAgoEnd.toISOString(),
            }}
          />
          <FilterListItem
            label="This Year"
            value={{
              [`${dateField}@gte`]: subWeeks(
                startOfWeek(new Date()),
                52
              ).toISOString(),
            }}
          />
        </FilterList>
        <FilterList label="Region" icon={<PublicOutlinedIcon />}>
          <FilterListItem
            label="Ontario"
            value={{ 'region@eq': 'ontario' }}
          />
          <FilterListItem
            label="Quebec"
            value={{ 'region@eq': 'quebec' }}
          />
        </FilterList>
        <FilterList label="DEC" icon={<CheckCircleOutlineIcon />}>
          <FilterListItem
            label="Completed"
            value={{
              dec_ready: true,
            }}
          />
          <FilterListItem
            label="Not Completed"
            value={{
              dec_ready: false,
            }}
          />
        </FilterList>
        <FilterList label="Death Benefits" icon={<CheckCircleOutlineIcon />}>
          <FilterListItem
            label="Completed"
            value={{
              death_benefits_status: 'completed',
            }}
          />
          <FilterListItem
            label="Not Completed"
            value={{
              death_benefits_status: 'not_completed',
            }}
          />
          <FilterListItem
            label="Ineligible"
            value={{
              death_benefits_status: 'ineligible',
            }}
          />
        </FilterList>
        <FilterList label="Attestation" icon={<CheckCircleOutlineIcon />}>
          <FilterListItem
            label="Completed"
            value={{
              attestation_ready: true,
            }}
          />
          <FilterListItem
            label="Not Completed"
            value={{
              attestation_ready: false,
            }}
          />
        </FilterList>
        <FilterList label="Funeral Type" icon={<PersonOutlineIcon />}>
          <FilterListItem
              label="Pre-Need"
              value={{
                'funeral_date@': "is.null",
                'has_preneed': true
              }}
            />
            <FilterListItem
              label="Pre-Need Fruition"
              value={{
                'funeral_date@': "not.is.null",
                'has_preneed': true}}
            />
             <FilterListItem
              label="At-Need"
              value={{'has_atneed': true}}
            />
            </FilterList>


        <FilterList label="Owner" icon={<PersonOutlineIcon />}>
          {owners?.map((owner: any) => (
            <FilterListItem
              label={`${owner.name}`}
              value={{
                owner_id: `${owner.id}`,
              }}
            />
          ))}
        </FilterList>
        <FilterList label="Website" icon={<LanguageIcon />}>
          <FilterListItem
            label="Published"
            value={{
              is_live: true,
            }}
          />
          <FilterListItem
            label="Not Published"
            value={{
              is_live: false,
            }}
          />
        </FilterList>

        <FilterList label="Archived" icon={<Inventory2OutlinedIcon />}>
          <FilterListItem
            label="No"
            value={{ 'archived_at@is': 'null' }}
            isSelected={(_, filters) => filters['archived_at@is'] === 'null'}
            toggleFilter={(_, filters) => {
              const { 'archived_at@not.is': _not, 'archived_at@is': existing, ...rest } = filters;
              return existing === 'null' ? rest : { ...rest, 'archived_at@is': 'null' };
            }}
          />
          <FilterListItem
            label="Yes"
            value={{ 'archived_at@not.is': 'null' }}
            isSelected={(_, filters) => filters['archived_at@not.is'] === 'null'}
            toggleFilter={(_, filters) => {
              const { 'archived_at@is': _is, 'archived_at@not.is': existing, ...rest } = filters;
              return existing === 'null' ? rest : { ...rest, 'archived_at@not.is': 'null' };
            }}
          />
        </FilterList>

      </div>
    </div>
  );
};

/**
 * Opens the filter set from the list toolbar. The count on the trigger is what
 * makes a hidden filter panel safe: filters you can't see are filters you
 * forget are on, and then the list looks wrong for no visible reason.
 */
export const FuneralFiltersButton = () => {
  const { filterValues } = useListContext();
  const tabFilterKeys = [
    'region@eq',
    'region@neq',
    'has_preneed@eq',
    'has_preneed@neq',
    'archived_at@is',
    'archived_at@not.is',
  ];
  const activeCount = Object.keys(filterValues).filter(
    (key) => !tabFilterKeys.includes(key) && key !== 'combined_unaccent@ilike',
  ).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          aria-label="Filters"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Filters
          {activeCount > 0 && (
            <span className="ml-0.5 rounded-full bg-primary px-1.5 text-[10px] font-semibold tabular-nums text-primary-foreground">
              {activeCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <FuneralFilterPanel />
      </PopoverContent>
    </Popover>
  );
};
